import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { FileText, Clock, User, Archive, Trash2, UserX, UserCheck, Download, Settings } from 'lucide-react';

const AdminLogs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchLogs();
  }, [user, navigate, actionFilter, page]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      params.append('skip', page * 50);
      params.append('limit', 50);

      const res = await api.get(`/admin/logs?${params.toString()}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'suspend_user': return <UserX className="w-4 h-4 text-[#E5484D]" />;
      case 'unsuspend_user': return <UserCheck className="w-4 h-4 text-[#00C9A7]" />;
      case 'archive_record': return <Archive className="w-4 h-4 text-[#F0B429]" />;
      case 'delete_record': return <Trash2 className="w-4 h-4 text-[#E5484D]" />;
      case 'export_data': return <Download className="w-4 h-4 text-[#1B5AFF]" />;
      case 'update_settings': return <Settings className="w-4 h-4 text-[#8B5CF6]" />;
      default: return <FileText className="w-4 h-4 text-[#5B6472]" />;
    }
  };

  const filterActions = (
    <Select value={actionFilter} onValueChange={setActionFilter}>
      <SelectTrigger className="w-[180px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
        <SelectValue placeholder="Action Type" />
      </SelectTrigger>
      <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
        <SelectItem value="all" className="text-[#0A0F1E]">All Actions</SelectItem>
        <SelectItem value="suspend_user" className="text-[#0A0F1E]">Suspend User</SelectItem>
        <SelectItem value="unsuspend_user" className="text-[#0A0F1E]">Unsuspend User</SelectItem>
        <SelectItem value="archive_record" className="text-[#0A0F1E]">Archive Record</SelectItem>
        <SelectItem value="delete_record" className="text-[#0A0F1E]">Delete Record</SelectItem>
        <SelectItem value="export_data" className="text-[#0A0F1E]">Export Data</SelectItem>
        <SelectItem value="update_settings" className="text-[#0A0F1E]">Settings Change</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <AdminLayout 
      title="Audit Logs" 
      breadcrumbs={[{ label: 'Logs' }]}
      actions={filterActions}
    >
      <p className="text-[#5B6472] mb-6">{total} total log entries</p>

      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F5F8FF]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-mono uppercase tracking-wider text-[#5B6472]">Action</th>
              <th className="px-6 py-3 text-left text-xs font-mono uppercase tracking-wider text-[#5B6472]">Admin</th>
              <th className="px-6 py-3 text-left text-xs font-mono uppercase tracking-wider text-[#5B6472]">Target</th>
              <th className="px-6 py-3 text-left text-xs font-mono uppercase tracking-wider text-[#5B6472]">Details</th>
              <th className="px-6 py-3 text-left text-xs font-mono uppercase tracking-wider text-[#5B6472]">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8EDF5]">
            {logs.map((log) => (
              <tr key={log.id} className="hover:bg-[#F5F8FF]/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {getActionIcon(log.action_type)}
                    <span className="text-[#0A0F1E] text-sm font-mono">{log.action_type}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#0A0F1E] text-sm">{log.admin_email}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-[#5B6472] text-sm">{log.target_type}: {log.target_id?.substring(0, 8)}...</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-[#5B6472] text-sm">{log.details || '-'}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1 text-[#5B6472] text-sm">
                    <Clock className="w-3 h-3" />
                    {new Date(log.created_at).toLocaleString()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {logs.length === 0 && !loading && (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#5B6472] mx-auto mb-4 opacity-50" />
            <p className="text-[#5B6472]">No audit logs found</p>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-between items-center mt-4">
          <p className="text-[#5B6472] text-sm">
            Showing {page * 50 + 1}-{Math.min((page + 1) * 50, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="text-[#5B6472]"
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * 50 >= total}
              className="text-[#5B6472]"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminLogs;
